import {Trans} from '@lingui/macro';
import React, {PropsWithChildren} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {LearnMoreButton} from '../../../components/common/callToAction/learnMoreButton';
import {ExpressionPill} from '../../rules/ruleEditor/inputs/expressionPill';
import {useBaseEditorContext} from '../baseEditorContext';
import {buildExpressionName, Expression} from './expressionModel';
import {ExpressionState, ExpressionStateActions} from './expressionState';
import {SidebarBox, StyledMultipleButtonWrap} from './sidebarBox';

/*
 * Props.
 */

interface ExpressionsSidebarProps<S extends ExpressionState> {
  state: S;
  onSelectExpression: ExpressionStateActions['onSelectExpression'];
  onCreateClick: Handler;
  helpPageUrl?: string;
  onHelpClick?: Handler;
  isReadOnly?: boolean;
}

/*
 * Style.
 */

const StyledEmptyExpressionDiv = styled.div`
  padding: 12px 12px 12px 30px;

  color: ${(p) => p.theme.greys.shade60};

  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
`;

/*
 * Component.
 */

export const ExpressionsSidebar = <S extends ExpressionState>(
  props: PropsWithChildren<ExpressionsSidebarProps<S>>,
) => {
  const theme = useUserTheme();
  const {state, onSelectExpression, onCreateClick, helpPageUrl, onHelpClick, isReadOnly} = props;
  const {expressions} = state;

  const {hasExpressions} = useBaseEditorContext();

  if (!hasExpressions) {
    return null;
  }

  return (
    <SidebarBox
      icon={<Icon name="sparkles" color={theme.palette.teal.shade50} />}
      title={<Trans id="editor-sidebar-variables-title">Dynamic Variables</Trans>}
    >
      {renderExpressions(expressions, onSelectExpression)}
      <StyledMultipleButtonWrap>
        {helpPageUrl && (
          <LearnMoreButton
            variant={ButtonVariantsEnum.LINK}
            helpPageUrl={helpPageUrl}
            onClick={onHelpClick}
          />
        )}
        {!isReadOnly && (
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onCreateClick}>
            <Trans id="editor-sidebar-variables-create-new">Create new</Trans>
          </Button>
        )}
      </StyledMultipleButtonWrap>
    </SidebarBox>
  );
};

/*
 * Helpers.
 */

function renderExpressions(
  expressions: ReadonlyArray<Expression>,
  onSelect: ExpressionStateActions['onSelectExpression'],
) {
  if (expressions.length === 0) {
    return (
      <StyledEmptyExpressionDiv>
        <Trans id="editor-sidebar-no-expressions">None</Trans>
      </StyledEmptyExpressionDiv>
    );
  }

  return (
    <ul>
      {expressions.map((exp) => (
        <li key={exp.id}>
          <ChromelessButton onClick={() => onSelect(exp.id)}>
            <ExpressionPill label={buildExpressionName(exp)} type={exp.inferredType} dryRun={exp.dryRun} />
          </ChromelessButton>
        </li>
      ))}
    </ul>
  );
}
